import { useEffect, useState } from "react";
import { CalendarDays, MapPin, Clock, Sparkles, Laptop, Lightbulb, Rocket, ArrowRight, CheckCircle2, ChevronDown, Award, Cpu, LineChart, Store, LayoutDashboard, Wrench, Code2, ExternalLink } from "lucide-react";
import Nav from "@/components/Nav";
import MoolahCrmMockup from "@/components/MoolahCrmMockup";
import BuildFramework from "@/components/BuildFramework";
import FAQ from "@/components/FAQ";
import CountdownTimer from "@/components/CountdownTimer";
import RegistrationForm from "@/components/RegistrationForm";
import { getSettings } from "@/lib/api";

const buildables = [
  { icon: Store, title: "Business Website", desc: "Build professional websites for businesses and organizations." },
  { icon: Sparkles, title: "Landing Page", desc: "Create conversion-focused pages for products, services, campaigns, and events." },
  { icon: LineChart, title: "CRM", desc: "Create lead and customer management systems." },
  { icon: LayoutDashboard, title: "Dashboard", desc: "Turn business information into useful visual dashboards." },
  { icon: Wrench, title: "Internal Business Tool", desc: "Create applications that improve workflows and reduce repetitive work." },
  { icon: Rocket, title: "Web App / MVP", desc: "Turn startup and business ideas into working prototypes." },
];

const audiences = [
  { t: "Business Owners", d: "Build tools and systems around your own business." },
  { t: "Entrepreneurs", d: "Turn ideas into prototypes and MVPs." },
  { t: "Freelancers", d: "Add AI-assisted digital development to your service offering." },
  { t: "Professionals", d: "Build tools that improve productivity and workflows." },
  { t: "Students", d: "Develop practical AI and digital-building skills." },
  { t: "Aspiring Builders", d: "Start turning ideas into digital products without first mastering traditional programming." },
];

const curriculum = ["Vibe Coding Fundamentals","App Blueprinting","AI Prompting for Builders","UI/UX Structure","Database Fundamentals","CRUD Operations","Forms & Inputs","Workflows","Testing","Debugging with AI","Deployment","Monetization"];

const timeline = [
  { time: "8:00 AM", title: "Welcome + The New Way of Building" },
  { time: "8:30 AM", title: "Blueprint" },
  { time: "9:00 AM", title: "Prompt" },
  { time: "9:45 AM", title: "Build" },
  { time: "10:30 AM", title: "Connect" },
  { time: "11:15 AM", title: "Test" },
  { time: "11:45 AM", title: "Launch" },
  { time: "12:00 NN", title: "Bootcamp Completion" },
];

const bringItems = ["Laptop","Laptop charger","Internet-capable device","Business or app idea","Notebook — optional","Curiosity and willingness to build"];

export default function Landing() {
  const [settings, setSettings] = useState(null);

  const load = async () => { try { setSettings(await getSettings()); } catch(e) { console.error(e); } };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  if (!settings) {
    return <div className="min-h-screen flex items-center justify-center bg-[#06080F]"><div className="chip"><span className="pulse-dot"/> LOADING</div></div>;
  }

  const seats = settings.seats;
  const soldOut = seats.remaining <= 0;
  const eventDate = new Date(settings.event_date + "T00:00:00").toLocaleDateString("en-US", { weekday:"long", year:"numeric", month:"long", day:"numeric" });

  return (
    <div id="top" className="min-h-screen bg-[#06080F] text-white">
      <Nav/>

      {/* HERO */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 hero-glow overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40"/>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="chip mb-5"><span className="pulse-dot"/> Moolah Tech Presents</div>
            <div className="mb-5 flex items-center gap-3">
              <img src="https://customer-assets-jai6qajn.emergentagent.net/job_build-framework-27/artifacts/xu1ebv9g_MOOLAH%20LOGO.jpg" alt="Moolah Tech" className="h-12 w-12 rounded-xl object-cover shadow-[0_0_24px_rgba(56,189,248,0.35)]"/>
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-slate-500">Moolah Tech Solutions Corp.</div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
              Turn your idea into a<br/>
              <span className="brand-gradient">working web app</span> in one day.
            </h1>
            <p className="text-slate-400 mt-6 text-base sm:text-lg max-w-xl leading-relaxed">
              A hands-on Vibe Coding Bootcamp where you'll learn how to transform ideas into websites, web apps, CRMs, dashboards, and business systems using AI. <span className="text-white font-medium">No traditional coding experience required.</span>
            </p>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
              <MetaRow icon={<CalendarDays className="w-4 h-4"/>} label={eventDate.toUpperCase()}/>
              <MetaRow icon={<Clock className="w-4 h-4"/>} label={`${fmtTime(settings.start_time)} – ${fmtTime(settings.end_time)}`}/>
              <MetaRow icon={<MapPin className="w-4 h-4"/>} label={`${settings.venue_floor}, ${settings.venue_name}`}/>
            </div>

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <a href={soldOut ? "#register" : "#register"} data-testid="hero-reserve-btn" className="cta-primary text-sm sm:text-base">
                {soldOut ? "JOIN WAITLIST" : "RESERVE MY SEAT"}
              </a>
              <a href="#framework" className="cta-outline text-sm">EXPLORE THE BUILD FRAMEWORK</a>
            </div>

            <p className="text-slate-500 text-xs mt-4 font-mono uppercase tracking-widest">Hands-On · Beginner Friendly · Bring Your Laptop</p>
          </div>

          <div className="lg:col-span-5 relative">
            <MoolahCrmMockup/>
          </div>
        </div>
      </section>

      {/* LIVE URGENCY STRIP */}
      <section id="bootcamp" className="border-y border-cyan-500/10 bg-[#080C18]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 grid md:grid-cols-3 gap-6 items-center">
          <div>
            <div className="eyebrow">Live availability</div>
            <div data-testid="seat-counter-display" className="text-2xl sm:text-3xl font-bold mt-1">
              {soldOut ? "SOLD OUT" : `Only ${seats.remaining} seats remaining`}
            </div>
            <div className="text-slate-500 text-xs mt-1">Confirmed: {seats.confirmed} / {seats.capacity}</div>
          </div>
          <div>
            <div className="eyebrow">Early Bird ends in</div>
            <div className="mt-2"><CountdownTimer deadline={settings.early_bird_deadline} active={settings.early_bird_active}/></div>
          </div>
          <div className="flex md:justify-end">
            <a href="#register" className="cta-primary text-sm">
              {soldOut ? "JOIN WAITLIST" : `Reserve — ₱${settings.early_bird_active ? settings.early_bird_price : settings.regular_price}`}
            </a>
          </div>
        </div>
      </section>

      {/* THE BIG IDEA */}
      <section className="py-24 sm:py-28">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3">The Big Idea</div>
          <h2 className="text-3xl sm:text-5xl font-bold leading-[1.05]">
            The way we build software<br/>
            <span className="brand-gradient">is changing.</span>
          </h2>
          <p className="text-slate-400 mt-5 max-w-2xl">
            You no longer have to spend months learning traditional programming before turning an idea into something you can see, test, and improve. AI has changed the building process. But <span className="text-white">random prompting isn't a strategy.</span>
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-10">
            <div className="glass p-6">
              <div className="eyebrow text-slate-500">Old Way</div>
              <div className="mt-4 space-y-2.5">
                {["Idea","Find developer","Explain requirements","Wait","Revise","Spend more","Launch"].map((s,i)=>(
                  <div key={s} className="flex items-center gap-3 text-slate-400">
                    <div className="w-6 h-6 rounded-md bg-slate-500/10 border border-slate-500/20 flex items-center justify-center text-[10px] font-mono text-slate-500">{i+1}</div>
                    <span className="text-sm">{s}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass p-6 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-cyan-400/10 blur-3xl"/>
              <div className="eyebrow relative">AI-Assisted Way</div>
              <div className="mt-4 space-y-2.5 relative">
                {["Idea","Blueprint","Prompt","Build","Test","Improve","Launch"].map((s,i)=>(
                  <div key={s} className="flex items-center gap-3 text-cyan-100">
                    <div className="w-6 h-6 rounded-md bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center text-[10px] font-mono text-cyan-300">{i+1}</div>
                    <span className="text-sm">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-2xl sm:text-3xl font-bold mt-10 leading-tight">
            AI doesn't remove the need for thinking.<br/>
            <span className="brand-gradient">It changes who can build.</span>
          </p>
        </div>
      </section>

      <BuildFramework/>

      {/* BUILD ALONG */}
      <section id="build" className="py-24 sm:py-28 bg-[#080C18] border-y border-cyan-500/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3">Build-Along Experience</div>
          <h2 className="text-3xl sm:text-5xl font-bold leading-[1.05]">
            Don't just watch AI.<br/>
            <span className="brand-gradient">Build with it.</span>
          </h2>
          <p className="text-slate-400 mt-5 max-w-2xl">See how an idea moves from concept to a functional digital product using a structured AI-assisted building process.</p>

          <div className="mt-10 grid lg:grid-cols-2 gap-6 items-start">
            <div className="space-y-5">
              <div className="glass p-2 overflow-hidden">
                <video data-testid="buildalong-video" src="https://customer-assets-jai6qajn.emergentagent.net/job_build-framework-27/artifacts/clhzygmy_aibootcamp.mp4" autoPlay loop muted playsInline controls preload="metadata" className="w-full rounded-2xl object-cover max-h-[340px]"/>
              </div>
              <div className="glass p-6">
                <div className="text-xs font-mono text-cyan-300 tracking-widest uppercase">Featured project</div>
                <div className="text-2xl font-bold mt-2">Lead Management System</div>
                <div className="grid grid-cols-2 gap-2 mt-5">
                  {["Dashboard","Leads","Contacts","Pipeline","Tasks","Follow-Ups","Analytics"].map(x => (
                    <div key={x} className="bg-white/[0.03] border border-white/5 rounded-lg px-4 py-2.5 text-sm text-slate-300">{x}</div>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {["01 — Idea","02 — Blueprint","03 — Prompt","04 — Build","05 — Connect Data","06 — Test","07 — Launch"].map((s,i)=>(
                <div key={s} className="flex items-center justify-between glass px-5 py-4">
                  <div className="flex items-center gap-3">
                    <span className="text-cyan-300 font-mono text-xs">{s.split(" — ")[0]}</span>
                    <span className="text-white text-sm">{s.split(" — ")[1]}</span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-cyan-400 -rotate-90"/>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT CAN YOU BUILD */}
      <section className="py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3">One skill · Many possibilities</div>
          <h2 className="text-3xl sm:text-5xl font-bold leading-[1.05]">
            What you can build<br/>
            <span className="brand-gradient">after four hours.</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {buildables.map(b => (
              <div key={b.title} className="glass p-6 group hover:border-cyan-400/40">
                <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5 text-cyan-300"/>
                </div>
                <div className="text-lg font-semibold">{b.title}</div>
                <p className="text-sm text-slate-400 mt-2">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMERCIAL VALUE */}
      <section className="py-24 sm:py-28 bg-[#080C18] border-y border-cyan-500/10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3">Commercial Value</div>
          <h2 className="text-3xl sm:text-5xl font-bold leading-[1.05]">
            Don't just learn AI.<br/>
            <span className="brand-gradient">Learn a skill you can sell.</span>
          </h2>
          <p className="text-slate-400 mt-5 max-w-2xl">Businesses need websites, landing pages, dashboards, CRMs, automation systems, and custom digital tools. Learning how to build these gives you a practical digital skill that can potentially be offered as a service.</p>

          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            {[
              ["Landing Page","₱5,000 – ₱15,000+"],
              ["Business Website","₱10,000 – ₱30,000+"],
              ["CRM / Business System","₱20,000 – ₱75,000+"],
              ["Custom Web App / MVP","₱30,000 – ₱100,000+"],
            ].map(([t,v]) => (
              <div key={t} className="glass p-6 flex items-center justify-between">
                <div className="text-slate-300 text-sm sm:text-base">{t}</div>
                <div className="text-white font-bold text-xl">{v}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-6 max-w-3xl leading-relaxed">
            Illustrative project-value ranges only. Actual pricing and income depend on experience, project complexity, client requirements, market conditions, and your ability to deliver. No income is guaranteed.
          </p>
        </div>
      </section>

      {/* WHO SHOULD ATTEND */}
      <section className="py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3">Who should attend</div>
          <h2 className="text-3xl sm:text-5xl font-bold leading-[1.05]">
            You don't have to be a developer<br/>
            <span className="brand-gradient">to start building.</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {audiences.map(a => (
              <div key={a.t} className="glass p-6">
                <div className="text-lg font-semibold">{a.t}</div>
                <p className="text-slate-400 text-sm mt-2">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="agenda" className="py-24 sm:py-28 bg-[#080C18] border-y border-cyan-500/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3">The 4-hour Curriculum</div>
          <h2 className="text-3xl sm:text-5xl font-bold leading-[1.05]">
            Four hours.<br/>
            <span className="brand-gradient">One structured building experience.</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-10">
            {curriculum.map((c,i) => (
              <div key={c} className="glass px-4 py-4 flex items-center gap-3">
                <span className="text-cyan-300 font-mono text-[11px] tracking-widest">{String(i+1).padStart(2,"0")}</span>
                <span className="text-sm">{c}</span>
              </div>
            ))}
          </div>

          <div className="eyebrow mt-16 mb-3">Your 4-hour building journey</div>
          <h3 className="text-2xl sm:text-4xl font-bold">One focused morning of building.</h3>
          <div className="mt-8 grid md:grid-cols-2 gap-3">
            {timeline.map((tl,i) => (
              <div key={tl.time} className="flex items-center gap-4 glass px-5 py-4">
                <div className="text-cyan-300 font-mono text-sm w-16">{tl.time}</div>
                <div className="flex-1 text-white text-sm">{tl.title}</div>
                <div className="text-slate-600 text-xs font-mono">{String(i+1).padStart(2,"0")}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVENT DETAILS + WHAT TO BRING */}
      <section className="py-24 sm:py-28">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="glass p-8">
            <div className="eyebrow">Moolah Tech</div>
            <h3 className="text-3xl font-bold mt-2 leading-tight">1-Day Vibe Coding Bootcamp</h3>
            <p className="text-slate-400 text-sm mt-2">Build your first web app using AI</p>
            <div className="divider-thin my-6"/>
            <DetailRow icon={<CalendarDays className="w-4 h-4"/>} label="Date" value={eventDate}/>
            <DetailRow icon={<Clock className="w-4 h-4"/>} label="Time" value={`${fmtTime(settings.start_time)} – ${fmtTime(settings.end_time)}`}/>
            <DetailRow icon={<MapPin className="w-4 h-4"/>} label="Venue"
              value={<><div>{settings.venue_floor}</div><div>{settings.venue_name}</div><div>{settings.venue_address}</div></>}/>
            <div className="chip mt-6"><span className="pulse-dot"/> Limited Seats Only</div>
          </div>
          <div className="glass p-8">
            <div className="eyebrow">What to bring</div>
            <h3 className="text-2xl font-bold mt-2">Bring your laptop. Bring your idea.<br/><span className="brand-gradient">Let's build.</span></h3>
            <ul className="mt-6 space-y-3">
              {bringItems.map(b => (
                <li key={b} className="flex items-center gap-3 text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300"/> {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PRICING + REGISTRATION */}
      <section id="pricing" className="py-24 sm:py-28 bg-[#080C18] border-y border-cyan-500/10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3">Pricing</div>
          <h2 className="text-3xl sm:text-5xl font-bold leading-[1.05]">
            Ready to become <span className="brand-gradient">a builder?</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-4 mt-10">
            <div data-testid="pricing-card-earlybird" className={`glass p-8 relative ${settings.early_bird_active ? "border-cyan-400/60 shadow-[0_0_40px_rgba(56,189,248,0.15)]" : "opacity-60"}`}>
              {settings.early_bird_active && <div className="absolute -top-3 left-6 chip bg-cyan-400/15 text-cyan-200 border-cyan-400/40">Early Bird · Live Now</div>}
              <div className="text-sm text-slate-400">Early Bird</div>
              <div className="text-5xl font-black mt-2">₱{settings.early_bird_price}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mt-3">Limited slots</div>
              <ul className="mt-6 space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300"/> Full 4-hour bootcamp</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300"/> Certificate of completion</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300"/> BUILD Framework™ playbook</li>
              </ul>
            </div>
            <div data-testid="pricing-card-regular" className="glass p-8">
              <div className="text-sm text-slate-400">Regular Price</div>
              <div className="text-5xl font-black mt-2">₱{settings.regular_price}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-3">Applied after Early Bird ends</div>
              <ul className="mt-6 space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-slate-400"/> Full 4-hour bootcamp</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-slate-400"/> Certificate of completion</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-slate-400"/> BUILD Framework™ playbook</li>
              </ul>
            </div>
          </div>

          {/* GCASH PAYMENT */}
          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            <div className="glass p-8">
              <div className="eyebrow">Payment</div>
              <h3 className="text-2xl font-bold mt-2">Pay via GCash</h3>
              <div className="mt-6 flex flex-col sm:flex-row items-center gap-6">
                <div data-testid="gcash-qr-image" className="w-44 sm:w-52 rounded-2xl bg-white p-2.5 flex items-center justify-center flex-shrink-0 shadow-[0_0_30px_rgba(56,189,248,0.15)]">
                  {settings.gcash_qr_url ? (
                    <img src={settings.gcash_qr_url.startsWith("http") ? settings.gcash_qr_url : `${process.env.REACT_APP_BACKEND_URL}${settings.gcash_qr_url}`} alt="GCash QR" className="w-full h-auto object-contain rounded-lg"/>
                  ) : (
                    <div className="w-full aspect-[1080/2095] grid grid-cols-8 gap-[2px]">
                      {Array.from({length: 64}).map((_,i)=>(
                        <div key={i} className={((i*7+i%5)%3===0 || i<8 || i>55 || i%8===0 || i%8===7) ? "bg-black" : "bg-white"}/>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Send to</div>
                  <div className="text-2xl font-bold text-white mt-1 tabular-nums whitespace-nowrap">{settings.gcash_number}</div>
                  <div className="text-xs text-slate-500 mt-3 max-w-[220px]">Scan the QR or send to the number, then submit your reference number and screenshot below.</div>
                </div>
              </div>

              <ol className="mt-8 space-y-3 text-sm text-slate-300 list-decimal list-inside">
                <li>Pay the applicable fee via GCash.</li>
                <li>Save your successful transaction screenshot.</li>
                <li>Complete the registration form.</li>
                <li>Enter your GCash reference number.</li>
                <li>Upload your payment screenshot.</li>
                <li>Wait for payment verification.</li>
              </ol>
              <p className="text-cyan-200 text-xs mt-5 font-mono uppercase tracking-widest">Your seat is officially confirmed only after payment verification.</p>
            </div>

            <RegistrationForm settings={settings} onDone={load}/>
          </div>
        </div>
      </section>

      <FAQ/>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-30"/>
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-cyan-500/10 blur-3xl"/>
        <div className="max-w-4xl mx-auto px-5 lg:px-8 relative text-center">
          <div className="chip mx-auto mb-6"><span className="pulse-dot"/> Idea → Build → Live App</div>
          <h2 className="text-4xl sm:text-6xl font-black leading-[1.02]">
            Your idea doesn't need<br/>
            <span className="brand-gradient">to stay an idea.</span>
          </h2>
          <p className="text-slate-400 mt-6 max-w-xl mx-auto">Give yourself one focused morning to learn how to turn ideas into digital products using AI.</p>
          <div className="glass mt-10 p-6 sm:p-8 text-left max-w-xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <img src="https://customer-assets-jai6qajn.emergentagent.net/job_build-framework-27/artifacts/xu1ebv9g_MOOLAH%20LOGO.jpg" alt="Moolah Tech" className="h-10 w-10 rounded-lg object-cover shadow-[0_0_16px_rgba(56,189,248,0.3)]"/>
              <div className="eyebrow">Moolah Tech</div>
            </div>
            <div className="text-xl font-bold mt-1">1-Day Vibe Coding Bootcamp</div>
            <div className="divider-thin my-4"/>
            <div className="text-sm text-slate-300 space-y-1">
              <div>{eventDate}</div>
              <div>{fmtTime(settings.start_time)} – {fmtTime(settings.end_time)}</div>
              <div>{settings.venue_floor}, {settings.venue_name}</div>
              <div className="text-slate-500">{settings.venue_address}</div>
            </div>
          </div>
          <div className="mt-8">
            <a href="#register" className="cta-primary text-base">{soldOut ? "JOIN WAITLIST" : "RESERVE MY SEAT"}</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets-jai6qajn.emergentagent.net/job_build-framework-27/artifacts/xu1ebv9g_MOOLAH%20LOGO.jpg" alt="Moolah Tech" className="h-9 w-9 rounded-lg object-cover shadow-[0_0_16px_rgba(56,189,248,0.3)]"/>
            <div className="text-slate-500 text-sm">© {new Date().getFullYear()} Moolah Tech. All rights reserved.</div>
          </div>
          <div className="flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-slate-500">
            <a href="mailto:hello@moolahtech.com" className="hover:text-cyan-300">Contact</a>
            <a href="/admin" className="hover:text-cyan-300 inline-flex items-center gap-1">Admin <ExternalLink className="w-3 h-3"/></a>
          </div>
        </div>
      </footer>

      {/* STICKY MOBILE CTA */}
      <div className="lg:hidden fixed bottom-3 inset-x-3 z-40">
        <a href="#register" data-testid="mobile-sticky-cta-btn"
          className="flex items-center justify-between glass px-4 py-3 border-cyan-400/40 shadow-[0_10px_30px_rgba(56,189,248,0.25)]">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">{soldOut ? "Limited" : `${seats.remaining} seats left`}</div>
            <div className="text-white text-sm font-semibold">
              {soldOut ? "Join Waitlist" : `Reserve — ₱${settings.early_bird_active ? settings.early_bird_price : settings.regular_price}`}
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-cyan-300"/>
        </a>
      </div>
    </div>
  );
}

function MetaRow({icon,label}) {
  return (
    <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-300 bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2">
      <span className="text-cyan-300">{icon}</span>
      <span className="font-mono tracking-wide">{label}</span>
    </div>
  );
}

function DetailRow({icon,label,value}) {
  return (
    <div className="flex items-start gap-4 mb-4">
      <div className="w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 flex-shrink-0">{icon}</div>
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-white text-sm mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function fmtTime(t) {
  if (!t) return "";
  const [h,m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h+11)%12)+1;
  return `${h12}:${String(m).padStart(2,"0")} ${ampm === "AM" && h===12 ? "NN" : ampm==="PM" && h===12 ? "NN" : ampm}`;
}
